import React, { useState } from "react";
import ReactMarkdown from "react-markdown";

import { Box, Chip, Modal, useMediaQuery, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";

function ProductDetailPara({ productDescription }) {
  const [showMore, setShowMore] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  // Word limit for collapsed state
  const wordLimit = 40;

  // Trimmed version of the description
  const getTrimmedDescription = () => {
    const trimmed = productDescription
      ?.split(' ')
      .slice(0, wordLimit)
      .join(' ');

    if (isMdUp && shouldTrim) {
      // For large devices, add inline "Show more" link
      return trimmed + '... ';
    }
    return trimmed + '...';
  };

  const shouldTrim = productDescription?.split(' ').length > wordLimit;

  // Handle show more click
  const handleShowMore = () => {
    if (isMdUp) {
      setModalOpen(true);
    } else {
      setShowMore(true);
    }
  };

  // Handle show less click
  const handleShowLess = () => {
    if (isMdUp) {
      setModalOpen(false);
    } else {
      setShowMore(false);
    }
  };

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {
      xs: '90%',
      sm: '80%',
      md: '70%',
      lg: '60%'
    },
    maxHeight: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: { xs: 2, sm: 3, md: 4 },
    overflow: 'auto',
  };

  // Common markdown styles
  const markdownStyles = {
    fontSize: {
      xs: "1.125rem",
      md: '1.325rem'
    },
    lineHeight: 1.7,
    color: "text.primary",
    fontFamily: "inherit",
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      mt: 2,
    },
    "& th, & td": {
      border: "1px solid #ccc",
      padding: "8px",
      textAlign: "justify",
    },
    "& ul, & ol": {
      paddingLeft: "20px",
      marginBottom: "1em",
    },
    "& a": {
      color: "primary.main",
      textDecoration: "underline",
      wordBreak: "break-word",
      fontSize: {
        xs: "1.125rem",
        md: '1.325rem'
      },
      lineHeight: 1.6,
      fontWeight: 400,
      display: "inline",
      verticalAlign: "baseline",
      textAlign: "justify",
      letterSpacing: "0.25px",
    },
    "& p": {
      textAlign: "justify",
    }
  };

  return (
    <>
      {/* Main content - shown for all devices */}
      <Box sx={markdownStyles}>
        {isMdUp ? (
          // For large devices: Show inline "Show more" link
          <>
            <ReactMarkdown>
              {shouldTrim ? getTrimmedDescription() : productDescription}
            </ReactMarkdown>
            {shouldTrim && (
              <Box
                component="span"
                onClick={handleShowMore}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: 'primary.dark',
                  },
                }}
              >Show more</Box>
            )}
          </>
        ) : (
          // For small devices: Show normal content with chip button
          <ReactMarkdown>
            {showMore || !shouldTrim ? productDescription : getTrimmedDescription()}
          </ReactMarkdown>
        )}
      </Box>

      {/* Show more/less chip button - only for small devices */}
      {!isMdUp && shouldTrim && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Chip
            label={showMore ? "Show less" : "Show more"}
            onClick={showMore ? handleShowLess : handleShowMore}
            icon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 2, cursor: "pointer" }}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {/* Modal for md and up devices */}
      <Modal
        open={modalOpen}
        onClose={handleShowLess}
        aria-labelledby="product-description-modal"
        aria-describedby="full-product-description"
      >
        <Box sx={modalStyle}>
          {/* Modal Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Product Details
            </Typography>
            <IconButton
              onClick={handleShowLess}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={markdownStyles}>
            <ReactMarkdown>
              {productDescription}
            </ReactMarkdown>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default ProductDetailPara;
